import React from 'react';
import { useState, useEffect } from 'react';
import { Alert, Card, CardContent, TextField, MenuItem, Select } from '@mui/material';
import * as yup from 'yup';
import { useDispatch } from 'react-redux';
import { showNotification } from '../../../redux/slices/notificationSlice';
import CustomButton from '../../../componentsII/Buttons';
import api from '../../../axios/api';

const schema = yup.object().shape({
  vehicleTypeName: yup.string().required("Vehicle Type is required"),
  vehicleSubTypeName: yup
    .string()
    .required('vehicle Sub Type name is required')
    .min(2, 'Vehicle Sub Type name must be at least 2 characters long')
    .max(50, 'Vehicle Sub Type name must be less than 50 characters')
    .trim()
});

const AddUpdateVehicleModal = ({ refetch, onClose, vehicleSubTypeId = null, initialData = null }) => {
  const dispatch = useDispatch();

  // Form states
  const [vehicleTypes, setVehicleTypes] = useState([]);
  const [vehicleTypeName, setvehicleTypeName] = useState(initialData?.type_name || '');
  const [vehicleSubTypeName, setvehicleSubTypeName] = useState(initialData?.sub_type_name || '');

  // UI states
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({
    vehicleSubTypeName: ''
  });

  const validateForm = async () => {
    try {
      await schema.validate({
        vehicleSubTypeName,
        vehicleTypeName: vehicleTypeName
      }, { abortEarly: false });
      return true;
    } catch (err) {
      const newErrors = {};
      err.inner.forEach((error) => {
        newErrors[error.path] = error.message;
      });
      setErrors(prevErrors => ({
        ...prevErrors,
        ...newErrors
      }));
      return false;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({ vehicleSubTypeName: '' });

    const isValid = await validateForm();
    if (!isValid) return;

    try {
      setIsSubmitting(true);

      const selectedVehicleTypeObj = vehicleTypes.find(vehicleType => vehicleType.type_name === vehicleTypeName);
      const payload = {
        VehicleTypeId:selectedVehicleTypeObj?.id,
        vehicleSubTypeId: vehicleSubTypeId || 0,
        vehicleSubTypeName: vehicleSubTypeName.trim(),
      };

      const response = await api.post('/admin/createUpdateVehicleSubType', payload);

      if (response.data.status === '200') {
        dispatch(showNotification({
          message: response.data.message,
          type: "success"
        }));
        onClose();
        refetch();
      } else {
        setErrors(prev => ({
          ...prev,
          general: response.data.message || 'Something went wrong'
        }));
      }
    } catch (err) {
      setErrors(prev => ({
        ...prev,
        general: err.message || 'An error occurred while saving'
      }));
      dispatch(showNotification({
        message: err.message || 'An error occurred',
        type: "error"
      }));
    } finally {
      setIsSubmitting(false);
    }
  };

  const getVehicleType = async () => {
    try {
      const response = await api.get('/admin/getVehicleTypes');
      if (response.data.data) {
        setVehicleTypes(response.data.data);
      }
    } catch (err) {
      dispatch(showNotification({
        message: 'Failed to fetch vehicle types',
        type: "error"
      }));
    }
  };

  useEffect(() => {
    getVehicleType();
  }, []);


  return (
    <Card className="w-full mx-auto">
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">

          <div className="space-y-2">
            <label htmlFor="vehicleTypeName" className="text-sm font-medium">
              Vehicle Type
            </label>
            <Select
              id="vehicleTypeName"
              value={vehicleTypeName}
              onChange={(e) => setvehicleTypeName(e.target.value)}
              placeholder="Enter Vehicle Type"
              disabled={isSubmitting}
              size="small"
              error={Boolean(errors.vehicleTypeName)}
              className="w-full"
            >
              {vehicleTypes.map((vehicleType) => (
                <MenuItem key={vehicleType.id} value={vehicleType.type_name}>
                  {vehicleType.type_name}
                </MenuItem>
              ))}
            </Select>
            {errors.vehicleTypeName && (
              <p className="text-sm text-red-500 mt-1">{errors.vehicleTypeName}</p>
            )}
          </div>

          <div className="space-y-2">
            <label htmlFor="vehicleSubTypeName" className="text-sm font-medium">
              Vehicle Sub Type Name
            </label>
            <TextField
              id="vehicleSubTypeName"
              value={vehicleSubTypeName}
              onChange={(e) => setvehicleSubTypeName(e.target.value)}
              placeholder="Enter sub-industry name"
              disabled={isSubmitting}
              size="small"
              error={Boolean(errors.vehicleSubTypeName)}
              className="w-full"
            />
            {errors.vehicleSubTypeName && (
              <p className="text-sm text-red-500 mt-1">{errors.vehicleSubTypeName}</p>
            )}
          </div>

          <div className="flex justify-end gap-5 items-center mt-5">
            <CustomButton
              className="bg-gray-500 hover:bg-gray-700 text-white"
              text="Cancel"
              click={onClose}
              disabled={isSubmitting}
            />
            <CustomButton
              className="bg-green-600 hover:bg-green-700 text-white"
              text={vehicleSubTypeId ? "Update" : "Submit"}
              type="submit"
              disabled={isSubmitting}
            />
          </div>
        </form>
      </CardContent>
    </Card>
  )
}

export default AddUpdateVehicleModal